// src/hooks/useTelegram.tsx
import { useToast } from '@/hooks/use-toast';
import { exchangeTelegramSession } from '@/lib/rpc/telegram-session';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

// Telegram WebApp types
interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
    secondary_bg_color?: string;
  };
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isProgressVisible: boolean;
    isActive: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  expand: () => void;
  close: () => void;
  ready: () => void;
  onEvent: (eventType: string, eventHandler: () => void) => void;
  offEvent: (eventType: string, eventHandler: () => void) => void;
  openTelegramLink: (url: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
}

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface TelegramContextType {
  webApp: TelegramWebApp | null;
  user: TelegramUser | null;
  isInTelegram: boolean;
  isAuthorized: boolean;
  isInitialized: boolean;
  tgUserId: number | null;
  customerId: string | null;
  startParam: string | null;
  authorize: () => Promise<boolean>;
  linkCustomer: (email?: string, bridgeCustomerId?: string) => Promise<boolean>;
  getPrefillData: () => {
    firstName: string;
    lastName: string;
    accountType: string;
    source: string;
  };
  hapticFeedback: {
    light: () => void;
    medium: () => void;
    heavy: () => void;
    success: () => void;
    error: () => void;
    warning: () => void;
    selection: () => void;
  };
  theme: {
    isDark: boolean;
    colors: {
      bg: string;
      text: string;
      hint: string;
      link: string;
      button: string;
      buttonText: string;
      secondaryBg: string;
    };
  };
}

const TelegramContext = createContext<TelegramContextType | null>(null);

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return context;
};

interface TelegramProviderProps {
  children: ReactNode;
}

export const TelegramProvider = ({ children }: TelegramProviderProps) => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [tgUserId, setTgUserId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [startParam, setStartParam] = useState<string | null>(null);
  const { toast } = useToast();

  const isInTelegram = !!(typeof window !== 'undefined' &&
    window.Telegram && window.Telegram.WebApp);

  useEffect(() => {
    if (!isInTelegram) {
      setIsInitialized(true);
      return;
    }

    const tg = window.Telegram.WebApp;
    setWebApp(tg);

    // Initialize Telegram WebApp
    tg.ready();
    tg.expand();

    // Helper to convert Hex to HSL
    const hexToHSL = (hex: string): string => {
      let r = 0, g = 0, b = 0;
      if (hex.length === 4) {
        r = parseInt("0x" + hex[1] + hex[1]);
        g = parseInt("0x" + hex[2] + hex[2]);
        b = parseInt("0x" + hex[3] + hex[3]);
      } else if (hex.length === 7) {
        r = parseInt("0x" + hex[1] + hex[2]);
        g = parseInt("0x" + hex[3] + hex[4]);
        b = parseInt("0x" + hex[5] + hex[6]);
      }
      r /= 255;
      g /= 255;
      b /= 255;
      const cmin = Math.min(r, g, b), cmax = Math.max(r, g, b), delta = cmax - cmin;
      let h = 0, s = 0, l = 0;

      if (delta === 0) h = 0;
      else if (cmax === r) h = ((g - b) / delta) % 6;
      else if (cmax === g) h = (b - r) / delta + 2;
      else h = (r - g) / delta + 4;

      h = Math.round(h * 60);
      if (h < 0) h += 360;

      l = (cmax + cmin) / 2;
      s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

      s = +(s * 100).toFixed(1);
      l = +(l * 100).toFixed(1);

      return `${h} ${s}% ${l}%`;
    };

    // Apply Telegram theme to CSS variables
    const applyTheme = () => {
      const root = document.documentElement;
      const p = tg.themeParams;

      if (p.bg_color) root.style.setProperty('--background', hexToHSL(p.bg_color));
      if (p.text_color) root.style.setProperty('--foreground', hexToHSL(p.text_color));
      if (p.button_color) root.style.setProperty('--primary', hexToHSL(p.button_color));
      if (p.button_text_color) root.style.setProperty('--primary-foreground', hexToHSL(p.button_text_color));
      if (p.secondary_bg_color) {
        root.style.setProperty('--muted', hexToHSL(p.secondary_bg_color));
        root.style.setProperty('--secondary', hexToHSL(p.secondary_bg_color));
      }
      if (p.hint_color) root.style.setProperty('--muted-foreground', hexToHSL(p.hint_color));

      // Optional: border color mapping (using hint color or derived)
      if (p.hint_color) root.style.setProperty('--border', hexToHSL(p.hint_color));
    };

    applyTheme();

    // Get user data
    if (tg.initDataUnsafe.user) {
      setUser(tg.initDataUnsafe.user);
      setTgUserId(tg.initDataUnsafe.user.id);
    }

    // Get start parameter
    if (tg.initDataUnsafe.start_param) {
      setStartParam(tg.initDataUnsafe.start_param);
    }

    // Handle theme changes
    const handleThemeChanged = () => {
      applyTheme();
      // Force re-render when theme changes by updating the webApp reference
      setWebApp(tg);
    };

    tg.onEvent('themeChanged', handleThemeChanged);

    setIsInitialized(true);

    return () => {
      tg.offEvent('themeChanged', handleThemeChanged);
    };
  }, [isInTelegram]);

  const authorize = useCallback(async (): Promise<boolean> => {
    if (!webApp || !webApp.initData) {
      return false;
    }

    try {
      const result = await exchangeTelegramSession(webApp.initData);

      if (!result.ok) {
        toast({
          title: "Authorization failed",
          description: result.error || "Failed to verify Telegram authentication",
          variant: "destructive",
        });
        return false;
      }

      // Session is set by exchangeTelegramSession
      // We verify the user ID from the context or session if needed, 
      // but here we just rely on the successful result.
      if (webApp.initDataUnsafe?.user?.id) {
        setTgUserId(webApp.initDataUnsafe.user.id);
      }
      setIsAuthorized(true);
      return true;

    } catch (e) {
      console.error("Telegram auth error:", e);
      return false;
    }
  }, [webApp, toast]);

  const linkCustomer = useCallback(async (email?: string, bridgeCustomerId?: string): Promise<boolean> => {
    if (!tgUserId) {
      return false;
    }

    try {
      const targetBridgeCustomerId = bridgeCustomerId || startParam || undefined;

      const response = await fetch('/api/telegram/link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tg_user_id: tgUserId,
          email,
          bridge_customer_id: targetBridgeCustomerId
        }),
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      if (result?.customer_id) {
        setCustomerId(result.customer_id);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }, [tgUserId, startParam]);

  // Helper to get prefilled data from URL params (when coming from Telegram deep link)
  const getPrefillData = () => {
    if (typeof window === 'undefined') return {
      firstName: '',
      lastName: '',
      accountType: '',
      source: '',
    };

    const params = new URLSearchParams(window.location.search);
    return {
      firstName: params.get('firstName') || user?.first_name || '',
      lastName: params.get('lastName') || user?.last_name || '',
      accountType: params.get('accountType') || '',
      source: params.get('source') || '',
    };
  };

  // Helper to compare Telegram WebApp versions (e.g., "6.1" >= "6.1")
  const isVersionAtLeast = (minVersion: string): boolean => {
    if (!webApp?.version) return false;
    const [currentMajor, currentMinor = 0] = webApp.version.split('.').map(Number);
    const [minMajor, minMinor = 0] = minVersion.split('.').map(Number);
    return currentMajor > minMajor || (currentMajor === minMajor && currentMinor >= minMinor);
  };

  // Haptic feedback helpers (requires Telegram WebApp 6.1+)
  // Silently no-op on older versions to avoid console warnings
  const hapticFeedback = {
    light: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.impactOccurred('light');
    },
    medium: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.impactOccurred('medium');
    },
    heavy: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.impactOccurred('heavy');
    },
    success: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.notificationOccurred('success');
    },
    error: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.notificationOccurred('error');
    },
    warning: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.notificationOccurred('warning');
    },
    selection: () => {
      if (isVersionAtLeast('6.1')) webApp?.HapticFeedback?.selectionChanged();
    },
  };

  // Theme helpers
  const theme = {
    isDark: webApp?.colorScheme === 'dark',
    colors: {
      bg: webApp?.themeParams.bg_color || 'hsl(var(--background))',
      text: webApp?.themeParams.text_color || 'hsl(var(--foreground))',
      hint: webApp?.themeParams.hint_color || 'hsl(var(--muted-foreground))',
      link: webApp?.themeParams.link_color || 'hsl(var(--primary))',
      button: webApp?.themeParams.button_color || 'hsl(var(--primary))',
      buttonText: webApp?.themeParams.button_text_color || 'hsl(var(--primary-foreground))',
      secondaryBg: webApp?.themeParams.secondary_bg_color || 'hsl(var(--muted))',
    }
  };

  const value: TelegramContextType = {
    webApp,
    user,
    isInTelegram,
    isAuthorized,
    isInitialized,
    tgUserId,
    customerId,
    startParam,
    authorize,
    linkCustomer,
    hapticFeedback,
    theme,
    getPrefillData,
  };

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
};

// Type declaration for global Telegram object
declare global {
  interface Window {
    Telegram: {
      WebApp: TelegramWebApp;
    };
  }
}